import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'environments/environment';
import { ApiService } from './api.service';
import {
  ArsConsultNoteImport,
  ArsConsultNoteImportDetail,
  ArsConsultNoteImportFilters
} from '../models/ars-consult-note-import.model';
import { LimitOffsetPagingMeta } from '../models/paged-results/limit-offset-paging.model';
import { PagedResults } from '../models/paged-results/paged-results.model';

export class PagedArsConsultNoteImports implements PagedResults {
  arsConsultNoteImports: ArsConsultNoteImport[];
  meta: LimitOffsetPagingMeta;

  get records() {
    return this.arsConsultNoteImports;
  }
}

@Injectable({
  providedIn: 'root'
})
export class ArsConsultNoteImportsService extends ApiService {
  private http = inject(HttpClient);

  get(id: number): Observable<ArsConsultNoteImportDetail> {
    return this.http.get<ArsConsultNoteImportDetail>(`${environment.captureApi.url}/ars_consult_note_imports/${id}`);
  }

  assignOffice(id: number, officeId: number): Observable<ArsConsultNoteImportDetail> {
    return this.http.patch<ArsConsultNoteImportDetail>(
      `${environment.captureApi.url}/ars_consult_note_imports/${id}/assign_office`,
      { arsConsultNoteImport: { officeId } }
    );
  }

  restart(id: number, step: 'ocr' | 'llm'): Observable<ArsConsultNoteImportDetail> {
    return this.http.patch<ArsConsultNoteImportDetail>(
      `${environment.captureApi.url}/ars_consult_note_imports/${id}/restart_${step}`,
      {}
    );
  }

  getList(
    paging: { page: number; pageSize: number } = null,
    filters: ArsConsultNoteImportFilters = null
  ): Observable<PagedArsConsultNoteImports> {
    const params = new URLSearchParams();

    if (filters?.requiresIntervention) {
      params.append('requires_intervention', 'true');
    }

    if (filters?.patientFirstName?.trim()) {
      params.append('patient_first_name', filters.patientFirstName.trim());
    }

    if (filters?.patientLastName?.trim()) {
      params.append('patient_last_name', filters.patientLastName.trim());
    }

    if (filters?.prescriberNpi?.trim()) {
      params.append('prescriber_npi', filters.prescriberNpi.trim());
    }

    filters?.selectedClientIds?.forEach(clientId => params.append('client_ids[]', clientId.toString()));

    if (paging) {
      params.append('page', paging.page.toString());
      params.append('per_page', paging.pageSize.toString());
    }

    return this.http.get<PagedArsConsultNoteImports>(
      `${environment.captureApi.url}/ars_consult_note_imports?${params.toString()}`
    );
  }

  upload(signedId: string): Observable<{ arsConsultNoteImports: ArsConsultNoteImport[] }> {
    return this.http.post<{ arsConsultNoteImports: ArsConsultNoteImport[] }>(
      `${environment.captureApi.url}/ars_consult_note_imports/upload`,
      { arsConsultNoteImport: { signedId } }
    );
  }
}
